import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
    Button,
    Checkbox,
    ConfigProvider,
    DatePicker,
    Input,
    Modal,
    Popover,
    Segmented,
    Table,
    Tooltip,
    Typography,
    theme,
} from "antd";
import { EditOutlined, EyeOutlined, FormOutlined, InfoCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { formattedPlayerName } from "@utils/name_utils";
import { useStatusTrackerV2 } from "../hooks/useStatusTrackerV2";
import { FlagTag } from "../views/FlagTag";
import { SingleDayTable, UnsavedPill } from "./SingleDayTable";
import { CoachFlagViewTable } from "./CoachFlagViewTable";
import {
    createBulkSelection,
    keyOf,
    selectionOf,
    useCellNote,
    useCellSelected,
    useSelectionVersion,
} from "../hooks/useBulkTagSelection";
import { useTableBodyHeight } from "../hooks/useTableBodyHeight";
import { getDatesToQuery } from "../state/utils";
import { selectCurrentCells, selectFilteredPlayers } from "../state/selectors";
import { coachFlagRowsFor, coachTagsFor, resolveBulkChanges, selectCoachFlags } from "../mappers/coachTag.mapper";

const { Text } = Typography;
const { TextArea } = Input;

const PLAYER_COL_WIDTH = 240;
const DATE_COL_WIDTH = 64;

const MODE_OPTIONS = [
    { label: "Month", value: "multi" },
    { label: "Day", value: "single" },
];

const todayStr = () => dayjs().format("YYYY-MM-DD");

// One grid cell: the tag checkbox and, once checked, a note button that opens a textarea. The
// draft stays local while typing and is written to the store when the popover closes, so a
// keystroke re-renders nothing but the textarea.
const BulkCell = React.memo(({ store, cellKey }) => {
    const checked = useCellSelected(store, cellKey);
    const note = useCellNote(store, cellKey);
    const [noteOpen, setNoteOpen] = useState(false);
    const [draft, setDraft] = useState("");

    const toggleNote = (open) => {
        if (open) setDraft(note);
        else store.setNote(cellKey, draft);
        setNoteOpen(open);
    };

    return (
        <div className="stv2-bulk-cell">
            <Checkbox checked={checked} onChange={(e) => store.setKeys([cellKey], e.target.checked)} />
            {checked && (
                <Popover
                    open={noteOpen}
                    onOpenChange={toggleNote}
                    trigger="click"
                    placement="bottom"
                    destroyTooltipOnHide
                    content={
                        <div className="stv2-bulk-note-popover">
                            <Text strong>Note for this date</Text>
                            <TextArea
                                autoFocus
                                value={draft}
                                onChange={(e) => setDraft(e.target.value)}
                                placeholder="Overrides the note above"
                                rows={3}
                                maxLength={5000}
                            />
                            <Button size="small" type="primary" onClick={() => toggleNote(false)}>
                                Done
                            </Button>
                        </div>
                    }
                >
                    <Tooltip title={note || "Add a note for this date"} mouseEnterDelay={0.4}>
                        <Button
                            type="text"
                            size="small"
                            icon={<FormOutlined />}
                            className={`stv2-bulk-note-btn${note ? " stv2-bulk-note-btn--set" : ""}`}
                            aria-label="Note for this date"
                        />
                    </Tooltip>
                </Popover>
            )}
        </div>
    );
});

// Column header: the month and day, plus a checkbox for every player on that date.
const DateHeader = ({ store, dt, keys }) => {
    useSelectionVersion(store);
    const { all, some } = selectionOf(store, keys);
    return (
        <div className="stv2-bulk-date-header">
            <span className="stv2-bulk-date-mon">{dayjs(dt).format("MMM")}</span>
            <span className="stv2-bulk-date-day">{dayjs(dt).format("DD")}</span>
            <Tooltip title="Every player on this date">
                <Checkbox checked={all} indeterminate={some} onChange={(e) => store.setKeys(keys, e.target.checked)} />
            </Tooltip>
        </div>
    );
};

// Frozen first column: the player plus a checkbox for every date in the row.
const PlayerHead = ({ store, player, keys, teamName }) => {
    useSelectionVersion(store);
    const { all, some } = selectionOf(store, keys);
    return (
        <div className="stv2-bulk-player">
            <Tooltip title="Every date for this player">
                <Checkbox checked={all} indeterminate={some} onChange={(e) => store.setKeys(keys, e.target.checked)} />
            </Tooltip>
            <span>
                {formattedPlayerName(teamName, `${player.first_name} ${player.last_name}`)}{" "}
                <Text type="secondary">#{player.number}</Text>
            </span>
        </div>
    );
};

const COUNT_LABEL = {
    multi: (n) => `${n} ${n === 1 ? "cell" : "cells"} selected`,
    single: (n) => `${n} tagged`,
};

const HINT = {
    multi: "Tag players on any day of the month.",
    single: "Every player's tag and note for this day.",
};

// How to use the dialog, under its title.
const HOW_TO = {
    edit:
        "Pick a period and a flag, then check the players to tag and add a note where you need one. " +
        "Use View only to see every coach flag in the period.",
    view: "Every coach flag in the period, one row per player and flag. Check flags to filter the rows, or click Edit to change the tags.",
};

const VIEW_HINT = {
    multi: "Every coach flag tagged this month.",
    single: "Every coach flag tagged on this day.",
};

const SelectionCount = ({ store, label }) => {
    useSelectionVersion(store);
    return <Text type="secondary">{label(store.snapshot().selected.size)}</Text>;
};

// Bulk tagging, in two modes over the same players the grid is showing (its filters apply).
// Multi day: one column per day of the tracker's month, read from the tracker's cache. Single
// day: one row per player for any past date, read from the dialog's own fetch. Picking a flag
// loads the cells already carrying it, with their notes, so the dialog edits what exists
// rather than only adding to it.
export const BulkTagModal = ({ open, onClose }) => {
    const { state, team, actions } = useStatusTrackerV2();
    const udfFlags = useSelector(({ userDefinedFlags }) => userDefinedFlags?.flags || []);
    const coachFlags = useMemo(() => selectCoachFlags(udfFlags), [udfFlags]);

    const { selectedMonth } = state.filters;
    // The tracker's selected month, capped at today — a coach flag cannot be tagged forward.
    // Same helper the month view fetches with, so the columns match the data the grid holds.
    const dateStrs = useMemo(() => getDatesToQuery(selectedMonth), [selectedMonth]);
    const cells = selectCurrentCells(state);
    const players = useMemo(
        () => selectFilteredPlayers(team.players, dateStrs, state.filters, team.playerGroups, state.statuses, cells),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [team.players, dateStrs, state.filters, team.playerGroups, state.statuses, state.byMonth]
    );
    const visibleKeys = useMemo(
        () => players.flatMap((p) => dateStrs.map((dt) => keyOf(p.player_id, dt))),
        [players, dateStrs]
    );

    const store = useMemo(() => createBulkSelection(), []);
    const { token } = theme.useToken();
    const [multiAreaRef, multiBodyHeight] = useTableBodyHeight();
    const [flagId, setFlagId] = useState(null);
    // Multi day: a note for every checked cell that has none of its own. It starts empty.
    const [commonNote, setCommonNote] = useState("");
    const [saving, setSaving] = useState(false);
    const [mode, setMode] = useState("multi");
    // A read-only table of every coach flag in the current mode's dates, with flag filters. It
    // stays on across a mode change.
    const [viewOnly, setViewOnly] = useState(false);
    const [filterIds, setFilterIds] = useState([]);
    const [day, setDay] = useState(todayStr);
    // The single-day fetch, tagged with its date so a result for an earlier date is never shown.
    const [dayLoad, setDayLoad] = useState({ day: null, cells: null, failed: false });
    const [dayAttempt, setDayAttempt] = useState(0);
    // A save keeps the dialog open. Each save reloads what the flag now carries; in single day
    // that means a new fetch, because a save does not patch the dialog's own day cells.
    const [saveCount, setSaveCount] = useState(0);
    const single = mode === "single";
    const dayCells = dayLoad.day === day ? dayLoad.cells : null;
    const dayFailed = dayLoad.day === day && dayLoad.failed;
    const dayKeys = useMemo(() => players.map((p) => keyOf(p.player_id, day)), [players, day]);
    const dayDates = useMemo(() => [day], [day]);

    // Start clean every time the dialog opens.
    useEffect(() => {
        if (open) return;
        setFlagId(null);
        setCommonNote("");
        setMode("multi");
        setViewOnly(false);
        setFilterIds([]);
        setDay(todayStr());
        store.reset();
    }, [open, store]);

    useEffect(() => {
        if (!open || !single) return undefined;
        let current = true;
        setDayLoad({ day, cells: null, failed: false });
        actions.fetchDayCells(day).then((fetched) => {
            if (current) setDayLoad({ day, cells: fetched, failed: !fetched });
        });
        return () => {
            current = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, single, day, dayAttempt, saveCount]);

    // Load what is already tagged with the picked flag in the current view, on every flag, mode
    // or day change, so the selection only ever holds cells the coach can see. Each loaded cell
    // keeps its own full note, and the common note starts empty: it must never put an existing
    // note on cells the coach checks later.
    useEffect(() => {
        const source = single ? dayCells : cells;
        const existing = flagId != null && source ? coachTagsFor(source, flagId, single ? dayKeys : visibleKeys) : {};
        setCommonNote("");
        store.reset({ selected: Object.keys(existing), notes: existing });
        // viewOnly: back in the edit table, edits discarded on the way into View only stay gone.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [flagId, single, dayCells, viewOnly, saveCount]);

    // A flag, mode or day change reloads the view. Ask first when that would drop changes a
    // save would make; with no flag picked there is nothing a save could make.
    const confirmDiscard = (apply) => {
        const { writes, removes } = changesFor();
        if (flagId == null || writes.length + removes.length === 0) {
            apply();
            return;
        }
        Modal.confirm({
            title: "Discard unsaved changes?",
            content: "Your changes to this flag have not been saved.",
            okText: "Discard",
            cancelText: "Keep editing",
            onOk: apply,
        });
    };

    // What a save would do, diffed against what picking the flag loaded. Unchecking every
    // cell leaves writes empty and removes full — a mass removal, which stays valid with a
    // flag picked and note text still in the box.
    const changesFor = () => {
        const { selected, notes } = store.snapshot();
        return resolveBulkChanges({
            selected,
            cellNotes: notes,
            commonNote,
            baseline: store.baseline(),
        });
    };

    const handleSave = async () => {
        const flag = coachFlags.find((f) => f.id === flagId);
        if (!flag) return;
        const { writes, removes } = changesFor();
        if (writes.length === 0 && removes.length === 0) return;
        setSaving(true);
        const saved = await actions.saveCoachFlagTagsBulk({ flag, writes, removes });
        setSaving(false);
        if (saved) setSaveCount((n) => n + 1);
    };

    // Selection lives in the store, so these never rebuild on a toggle or a note.
    const columns = useMemo(() => {
        const rowKeys = new Map(players.map((p) => [p.player_id, dateStrs.map((dt) => keyOf(p.player_id, dt))]));
        const playerColumn = {
            title: <div className="stv2-bulk-player">Player</div>,
            dataIndex: "player",
            key: "player",
            fixed: "left",
            width: PLAYER_COL_WIDTH,
            render: (player) => (
                <PlayerHead store={store} player={player} keys={rowKeys.get(player.player_id)} teamName={team.teamName} />
            ),
        };
        const dateColumns = dateStrs.map((dt) => {
            const colKeys = players.map((p) => keyOf(p.player_id, dt));
            return {
                key: dt,
                width: DATE_COL_WIDTH,
                align: "center",
                title: <DateHeader store={store} dt={dt} keys={colKeys} />,
                render: (_, row) => <BulkCell store={store} cellKey={keyOf(row.player.player_id, dt)} />,
            };
        });
        return [playerColumn, ...dateColumns];
    }, [dateStrs, players, team.teamName, store]);

    const rows = useMemo(() => players.map((player) => ({ key: player.player_id, player })), [players]);

    // A checked cell means "tag with the picked flag", so with no flag picked the checkboxes in
    // both tables are disabled. componentDisabled reaches every antd control inside the table;
    // a control with its own `disabled` prop overrides it.
    const noFlag = flagId == null;
    const hintText = viewOnly ? VIEW_HINT[mode] : noFlag ? "Pick a flag to start." : HINT[mode];
    const hint = (
        <Text type="secondary" className="stv2-bulk-hint">
            {hintText}{" "}
            <Tooltip title="Showing the players the tracker grid is filtered to.">
                <InfoCircleOutlined />
            </Tooltip>
        </Text>
    );

    // View only: the rows of the current mode's dates, with the flag filters applied. Built here
    // because the footer shows their count.
    const viewRows = useMemo(
        () =>
            viewOnly
                ? coachFlagRowsFor({
                      cells: (single ? dayCells : cells) || {},
                      players,
                      dates: single ? dayDates : dateStrs,
                      coachFlags,
                      checkedIds: filterIds,
                  })
                : [],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [viewOnly, single, dayCells, state.byMonth, players, dayDates, dateStrs, coachFlags, filterIds]
    );

    // View only has no edits, so Edit needs no confirm; the reload effect then loads the picked
    // flag from the day's data again.
    const toggleViewOnly = () => (viewOnly ? setViewOnly(false) : confirmDiscard(() => setViewOnly(true)));
    const toggleFilter = (id, on) => setFilterIds((ids) => (on ? [...ids, id] : ids.filter((x) => x !== id)));

    const multiBody =
        dateStrs.length === 0 ? (
            <Text type="secondary">
                {selectedMonth.format("MMMM YYYY")} has not started yet. Pick the current month or an earlier
                one in the tracker to tag players.
            </Text>
        ) : (
            <>
                <div className="stv2-bulk-day-toolbar">
                    {hint}
                    <UnsavedPill store={store} changesFor={changesFor} />
                </div>
                {viewOnly ? (
                    <CoachFlagViewTable
                        rows={viewRows}
                        filtered={filterIds.length > 0}
                        period="this month"
                        teamName={team.teamName}
                        fileName={`coach-flags-${selectedMonth.format("YYYY-MM")}`}
                    />
                ) : (
                    <div ref={multiAreaRef} className="stv2-bulk-table-area">
                        <ConfigProvider componentDisabled={noFlag}>
                            <Table
                                className="stv2-bulk-table stv2-bulk-grid"
                                // The checked-cell tint is antd's selected-row color.
                                style={{ "--stv2-row-on": token.controlItemBgActive }}
                                columns={columns}
                                dataSource={rows}
                                rowKey="key"
                                size="small"
                                bordered
                                virtual
                                pagination={false}
                                scroll={{ x: PLAYER_COL_WIDTH + DATE_COL_WIDTH * dateStrs.length, y: multiBodyHeight }}
                            />
                        </ConfigProvider>
                    </div>
                )}
            </>
        );

    // A failed day load shows no table: empty rows would read as "nobody is tagged", and a save
    // from there would write over the existing notes.
    const singleBody = dayFailed ? (
        <div className="stv2-bulk-day-error">
            <Text type="secondary">Could not load the flags for {dayjs(day).format("MMM D")}.</Text>
            <Button size="small" onClick={() => setDayAttempt((n) => n + 1)} data-attr="status-tracker.bulk-tag.reload">
                Try again
            </Button>
        </div>
    ) : (
        <>
            {hint}
            {viewOnly ? (
                <CoachFlagViewTable
                    rows={viewRows}
                    filtered={filterIds.length > 0}
                    period="on this day"
                    teamName={team.teamName}
                    fileName={`coach-flags-${day}`}
                    loading={!dayCells}
                />
            ) : (
                <SingleDayTable
                    store={store}
                    players={players}
                    day={day}
                    teamName={team.teamName}
                    loading={!dayCells}
                    disabled={noFlag}
                    changesFor={changesFor}
                />
            )}
        </>
    );

    return (
        <Modal
            title={
                <>
                    {viewOnly ? "Coach flags" : "Tag players with a coach flag"}
                    <Text type="secondary" className="stv2-bulk-subtitle">
                        {viewOnly ? HOW_TO.view : HOW_TO.edit}
                    </Text>
                </>
            }
            open={open}
            onCancel={onClose}
            width={1100}
            centered
            className="stv2-modal-root stv2-bulk-dialog"
            rootClassName="stv2-modal-root"
            // One body height in both modes, so the dialog does not jump on a mode change.
            classNames={{ body: coachFlags.length > 0 ? "stv2-bulk-modal-body" : undefined }}
            destroyOnClose
            footer={
                viewOnly ? (
                    <div className="stv2-bulk-footer">
                        <Text type="secondary">
                            {viewRows.length} {viewRows.length === 1 ? "flag" : "flags"}
                        </Text>
                        <Button onClick={onClose} data-attr="status-tracker.bulk-tag.close">
                            Close
                        </Button>
                    </div>
                ) : (
                    <div className="stv2-bulk-footer">
                        <SelectionCount store={store} label={COUNT_LABEL[mode]} />
                        <div className="stv2-bulk-footer-actions">
                            <Button onClick={onClose} disabled={saving}>
                                Cancel
                            </Button>
                            <SaveButton store={store} flagId={flagId} saving={saving} changesFor={changesFor} onSave={handleSave} />
                        </div>
                    </div>
                )
            }
        >
            {coachFlags.length === 0 ? (
                <Text type="secondary">No coach flags yet. Create one in Settings to tag players by hand.</Text>
            ) : (
                <>
                    <div className="stv2-bulk-modal-top">
                        <div className="stv2-bulk-field stv2-bulk-field--inline">
                            <Text strong className="stv2-bulk-label">
                                Period
                            </Text>
                            <div className="stv2-bulk-days">
                                {/* This row is antd's small size (24px): control, picker and button. */}
                                <Segmented
                                    size="small"
                                    options={MODE_OPTIONS}
                                    value={mode}
                                    onChange={(value) => confirmDiscard(() => setMode(value))}
                                />
                                {single ? (
                                    <DatePicker
                                        size="small"
                                        value={dayjs(day)}
                                        allowClear={false}
                                        disabledDate={(d) => d.isAfter(dayjs(), "day")}
                                        onChange={(d) => confirmDiscard(() => setDay(d.format("YYYY-MM-DD")))}
                                    />
                                ) : (
                                    // The tracker's month, which this view follows.
                                    <Text type="secondary" className="stv2-bulk-month">
                                        {selectedMonth.format("MMM YYYY")}
                                    </Text>
                                )}
                            </div>
                            <Button
                                className="stv2-bulk-view-toggle"
                                size="small"
                                icon={viewOnly ? <EditOutlined /> : <EyeOutlined />}
                                onClick={toggleViewOnly}
                                data-attr={viewOnly ? "status-tracker.bulk-tag.edit" : "status-tracker.bulk-tag.view"}
                            >
                                {viewOnly ? "Edit" : "View only"}
                            </Button>
                        </div>
                        <div className="stv2-bulk-field stv2-bulk-field--inline">
                            <Text strong className="stv2-bulk-label">
                                {viewOnly ? "Filter" : "Flag"}
                            </Text>
                            <div className="stv2-bulk-flag-tags">
                                {/* In View only the same pills are multi-select filters. */}
                                {coachFlags.map((f) => (
                                    <FlagTag
                                        key={f.id}
                                        flag={f}
                                        selected={viewOnly ? filterIds.includes(f.id) : flagId === f.id}
                                        onSelect={(id) => confirmDiscard(() => setFlagId(id))}
                                        onToggle={viewOnly ? toggleFilter : undefined}
                                    />
                                ))}
                            </div>
                        </div>
                        {!single && !viewOnly && (
                            <div className="stv2-bulk-field stv2-bulk-field--inline stv2-bulk-field--note">
                                <Text strong className="stv2-bulk-label">
                                    Note
                                </Text>
                                <TextArea
                                    size="small"
                                    value={commonNote}
                                    onChange={(e) => setCommonNote(e.target.value)}
                                    placeholder="Optional. Goes on every checked cell without its own note."
                                    autoSize={{ minRows: 1, maxRows: 3 }}
                                    maxLength={5000}
                                />
                            </div>
                        )}
                    </div>
                    {single ? singleBody : multiBody}
                </>
            )}
        </Modal>
    );
};

// Enabled once a flag is picked and the diff has something in it — which includes a save that
// only removes, so unchecking every cell still saves. Subscribes so it tracks the selection
// without the whole modal re-rendering, and pulses while there is something unsaved.
const SaveButton = ({ store, flagId, saving, changesFor, onSave }) => {
    useSelectionVersion(store);
    const { writes, removes } = changesFor();
    const canSave = flagId != null && writes.length + removes.length > 0;
    const dirty = canSave;
    return (
        <Button
            type="primary"
            onClick={onSave}
            loading={saving}
            disabled={!canSave}
            className={canSave && !saving && dirty ? "pulse" : ""}
        >
            Save
        </Button>
    );
};
